if (!customElements.get('contact-picker')) {
  class ContactPicker extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('.contact-picker__toggle');
      this.menu = this.querySelector('.contact-picker__menu');
      if (!this.toggle || !this.menu) return;

      this._supportsHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      this._longPressMs = 450;
      this._pressTimer = null;
      this._closeTimer = null;
      this._menuPlaceholder = document.createComment('contact-picker-menu-anchor');
      this.menu.parentNode.insertBefore(this._menuPlaceholder, this.menu);

      this._onToggleClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        this._isOpen() ? this.close() : this.open();
      };

      this._onDocumentClick = (event) => {
        if (!this.contains(event.target) && !this.menu.contains(event.target)) {
          this.close();
        }
      };

      this._onKeyDown = (event) => {
        if (event.key === 'Escape') this.close();
      };

      this._onReposition = () => {
        if (this._isOpen()) this._positionMenu();
      };

      this.toggle.addEventListener('click', this._onToggleClick);

      if (this._supportsHover) {
        this.toggle.addEventListener('mouseenter', () => this._cancelClose());
        this.toggle.addEventListener('mouseleave', () => this._scheduleClose());
        this.menu.addEventListener('mouseenter', () => this._cancelClose());
        this.menu.addEventListener('mouseleave', () => this._scheduleClose());
      } else {
        this.toggle.addEventListener(
          'touchstart',
          (event) => {
            this._pressTimer = window.setTimeout(() => {
              event.preventDefault();
              this.open();
            }, this._longPressMs);
          },
          { passive: false }
        );
        this.toggle.addEventListener('touchend', () => this._clearPressTimer());
        this.toggle.addEventListener('touchmove', () => this._clearPressTimer());
        this.toggle.addEventListener('touchcancel', () => this._clearPressTimer());
      }

      document.addEventListener('click', this._onDocumentClick);
      document.addEventListener('keydown', this._onKeyDown);
      window.addEventListener('resize', this._onReposition);
      window.addEventListener('scroll', this._onReposition, true);

      this.menu.querySelectorAll('a[href]').forEach((link) => {
        link.addEventListener('click', () => this.close());
      });
    }

    disconnectedCallback() {
      this._clearPressTimer();
      this._cancelClose();
      if (this.toggle) this.toggle.removeEventListener('click', this._onToggleClick);
      document.removeEventListener('click', this._onDocumentClick);
      document.removeEventListener('keydown', this._onKeyDown);
      window.removeEventListener('resize', this._onReposition);
      window.removeEventListener('scroll', this._onReposition, true);
      this.close();
    }

    _clearPressTimer() {
      if (this._pressTimer) {
        window.clearTimeout(this._pressTimer);
        this._pressTimer = null;
      }
    }

    _scheduleClose() {
      this._cancelClose();
      this._closeTimer = window.setTimeout(() => this.close(), 120);
    }

    _cancelClose() {
      if (this._closeTimer) {
        window.clearTimeout(this._closeTimer);
        this._closeTimer = null;
      }
    }

    _isOpen() {
      return this.classList.contains('is-open');
    }

    _positionMenu() {
      const rect = this.toggle.getBoundingClientRect();
      const gap = 8;
      const margin = 12;

      this.menu.style.position = 'fixed';
      this.menu.style.zIndex = '10000';
      this.menu.style.right = 'auto';
      this.menu.style.bottom = 'auto';

      const menuWidth = this.menu.offsetWidth || 280;
      let left = rect.left;

      if (left + menuWidth > window.innerWidth - margin) {
        left = window.innerWidth - menuWidth - margin;
      }
      if (left < margin) left = margin;

      let top = rect.bottom + gap;
      const menuHeight = this.menu.offsetHeight || 200;

      if (top + menuHeight > window.innerHeight - margin) {
        top = Math.max(margin, rect.top - menuHeight - gap);
      }

      this.menu.style.left = `${left}px`;
      this.menu.style.top = `${top}px`;
    }

    _attachMenuToBody() {
      if (this.menu.parentNode !== document.body) {
        document.body.appendChild(this.menu);
      }
      this.menu.classList.add('contact-picker__menu--floating');
    }

    _restoreMenu() {
      this.menu.classList.remove('contact-picker__menu--floating');
      this.menu.style.position = '';
      this.menu.style.top = '';
      this.menu.style.left = '';
      this.menu.style.right = '';
      this.menu.style.bottom = '';
      this.menu.style.zIndex = '';

      if (this._menuPlaceholder && this._menuPlaceholder.parentNode) {
        this._menuPlaceholder.parentNode.insertBefore(this.menu, this._menuPlaceholder);
      }
    }

    open() {
      this._cancelClose();
      this.classList.add('is-open');
      this.toggle.setAttribute('aria-expanded', 'true');
      this.menu.hidden = false;
      this._attachMenuToBody();
      this._positionMenu();
    }

    close() {
      this._cancelClose();
      if (!this._isOpen() && this.menu.hidden) return;

      this.classList.remove('is-open');
      this.toggle.setAttribute('aria-expanded', 'false');
      this.menu.hidden = true;
      this._restoreMenu();
    }
  }

  customElements.define('contact-picker', ContactPicker);
}
